import { Button } from '@/components/ui/button';
import { formatNumber } from '@/utils/number';
import {
  PCMP_MARKETING_TAB_COUNT,
  PCMP_MARKETING_TAB_LABELS,
} from '../../constants/pcmpMarketing.constants';
import {
  PCMP_MARKETING_TABS,
  PCMP_PROPOSAL_CORRECTION_TAB,
  type PcmpMarketingQueueCounts,
  type PcmpMarketingTab,
} from '../../types/pcmpMarketing.types';
import { pcmpSegmentClass } from '../../utils/pcmpSegmentClass';
import { PcmpQueueTabs } from '../PcmpQueue/PcmpQueueTabs';

interface PcmpMarketingTabsProps {
  tab: PcmpMarketingTab;
  counts: PcmpMarketingQueueCounts | undefined;
  onTabChange: (tab: PcmpMarketingTab) => void;
}

const EXPLAINED_OPTIONS = [
  { value: false, label: 'Awaiting explanation' },
  { value: true, label: 'Explained' },
  { value: undefined, label: 'Both' },
] as const;

export function PcmpMarketingTabs({ tab, counts, onTabChange }: PcmpMarketingTabsProps) {
  // Proposal Correction only for those the API offers it to (a null count);
  // kept while the counts load if it is the open tab.
  const showCorrection = counts
    ? counts.proposalCorrection !== null
    : tab === PCMP_PROPOSAL_CORRECTION_TAB;
  const tabs = showCorrection
    ? PCMP_MARKETING_TABS
    : PCMP_MARKETING_TABS.filter((value) => value !== PCMP_PROPOSAL_CORRECTION_TAB);

  return (
    <PcmpQueueTabs
      tabs={tabs}
      labels={PCMP_MARKETING_TAB_LABELS}
      value={tab}
      countOf={counts ? (value) => PCMP_MARKETING_TAB_COUNT[value](counts) : undefined}
      onChange={onTabChange}
    />
  );
}

interface PcmpExplainedToggleProps {
  explained: boolean | undefined;
  counts: PcmpMarketingQueueCounts | undefined;
  onChange: (explained: boolean | undefined) => void;
}

/** The MIS Not Verified tab's split: awaiting Marketing's explanation, or back with MIS. */
export function PcmpExplainedToggle({ explained, counts, onChange }: PcmpExplainedToggleProps) {
  const countOf = (value: boolean | undefined) => {
    if (!counts) return undefined;
    if (value === false) return counts.misNotVerified;
    if (value === true) return counts.misExplained;
    return counts.misNotVerified + counts.misExplained;
  };

  return (
    <div className="flex w-full max-w-full items-center overflow-x-auto rounded-lg border bg-muted/40 p-1 sm:w-fit">
      {EXPLAINED_OPTIONS.map((option) => {
        const count = countOf(option.value);
        return (
          <Button
            key={option.label}
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={explained === option.value}
            onClick={() => onChange(option.value)}
            className={pcmpSegmentClass(explained === option.value)}
          >
            {option.label}
            {count !== undefined ? (
              <span className="text-xs tabular-nums">{formatNumber(count)}</span>
            ) : null}
          </Button>
        );
      })}
    </div>
  );
}
